import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { loadUserflow, Userflow } from "userflow.js";
import { DefaultErrorFallback, withErrorBoundary } from "@fidget/common/components/errorBoundary";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import { config } from "@fidget/common/config/config";
import { CurrentClientContainer } from "../../components/dashboard/currentClientContainer/currentClientContainer";
import { ToastArea } from "../../components/toast/toastArea";
import { WidgetWrapper } from "../../components/widgetWrapper";
import { useStore } from "../../models";
import { InboxFilterId } from "../../models/inbox";
import { ReportsScope } from "../../models/reports/ReportsScope";
import { ToastMessageType } from "../../models/ui/toastModels";
import { AiSuggestions } from "../ai/aiSuggestions";
import { Calendar } from "../calendar/calendar";
import { CalendarDragOverlay } from "../calendar/calendarDragOverlay";
import { InboxContainer } from "../inbox/inbox";
import { Reports } from "../reports/reports";
import { SettingsContainer } from "../settings/settings";
import styles from "./dashboard.css";
import { AgentDashboardHeader } from "./header";
import { ModalContainer } from "./modalContainer";
import { ToastBar } from "./toastBar";
import { DashboardView } from "./views/DashboardView";
import { useAgentServices } from "../../models/agentServices";

interface Props {
  view: DashboardView;
}

interface IRouteProps {
  id?: string;
  reportsScope?: ReportsScope;
}

type IProps = Props & RouteComponentProps<IRouteProps>;

const AgentDashboard = observer((props: IProps) => {
  const { inboxStore, callStore, currentUserStore, uiStore } = useStore();
  const { spinnerService } = useAgentServices();
  const [tabTitleIntervalTimer, setTabTitleIntervalTimer] = useState<number>();

  const userflowIdentify = (userflow: Userflow) => {
    const { profile, orgConfig, planPrice, planCurrency, taxRate, planName, role } =
      currentUserStore;
    const { id, email, firstName, lastName } = profile!;
    const { name, baseUrl } = orgConfig!;

    const unitVat = taxRate ? Math.round(planPrice * taxRate * 100) / 100 : 0;
    const planPriceWithVat = planPrice + unitVat;
    const browserLanguage = window.navigator.language;

    userflow
      .identify(id, {
        name: firstName + " " + lastName,
        email: email || "",
        traits: {
          org_name: name,
          ...(baseUrl && { base_url: baseUrl! }),
          ...(firstName && { first_name: firstName! }),
          ...(lastName && { last_name: lastName! }),
          user_role: role as string,
          plan_price: planPriceWithVat || 0,
          plan_currency: planCurrency || "",
          plan_name: planName || "",
          browser_language: browserLanguage
        }
      })
      .finally(() => uiStore.checkUserflowButtonPresent());
  };

  const setupUserFlow = async () => {
    const userflow = await loadUserflow();
    userflow.init(config.services.userflowToken);
    if (userflow) {
      userflowIdentify(userflow);
    }
  };

  useEffect(() => {
    const checkSpinnerToast = async () => {
      const toast = await spinnerService.getToast();
      if (toast.status === "TrailStarted") {
        uiStore.addToastBarMessage(
          intl.formatMessage("paymentToast.startSuccess"),
          ToastMessageType.PAYMENT
        );
      }
    };

    if (config.services.userflowScriptEnabled) {
      setupUserFlow();
    }

    checkSpinnerToast();

    return () => {
      if (tabTitleIntervalTimer) {
        clearInterval(tabTitleIntervalTimer);
        setTabTitleIntervalTimer(undefined);
        document.title = intl.formatMessage("tab.title.default");
      }
    };
  }, []);

  // TODO: move this to uiStore
  const setTabTitle = () => {
    const unreadMessagesCount = inboxStore.ownUnreadCount;
    const receiving = callStore.isConnecting;

    let counter = false;

    if (!tabTitleIntervalTimer && (!!unreadMessagesCount || receiving)) {
      setTabTitleIntervalTimer(
        window.setInterval(function () {
          document.title = counter
            ? intl.formatMessage("tab.title.newMessage")
            : intl.formatMessage("tab.title.default");
          counter = !counter;
        }, 1000)
      );
    } else if (tabTitleIntervalTimer && !unreadMessagesCount && !receiving) {
      clearInterval(tabTitleIntervalTimer);
      setTabTitleIntervalTimer(undefined);
      document.title = intl.formatMessage("tab.title.default");
    }
  };

  useEffect(() => {
    setTabTitle();
  }, [inboxStore.ownUnreadCount, callStore.isConnecting]);

  const renderView = () => {
    switch (props.view) {
      case DashboardView.CONVERSATIONS:
        return (
          <>
            <InboxContainer />
            <CurrentClientContainer
              inboxFilter={inboxStore && (inboxStore.selectedFilter.id as InboxFilterId)}
              roomId={props.match.params.id}
            />
            <div className={styles.calendar}>
              <Calendar weekly={false} />
            </div>
          </>
        );
      case DashboardView.CALENDAR:
        return <Calendar weekly />;

      case DashboardView.AI_SETTINGS_GENERAL:
      case DashboardView.AI_SETTINGS_MODEL_CONFIGURATION:
      case DashboardView.AI_SETTINGS_TESTING:
        return <AiSuggestions view={props.view} />;

      case DashboardView.SETTINGS_PROFILE:
      case DashboardView.SETTINGS_SETTINGS:
      case DashboardView.SETTINGS_USERS:
      case DashboardView.SETTINGS_ADVISER_GROUPS:
      case DashboardView.SETTINGS_ADVISER_STATUSES:
      case DashboardView.SETTINGS_ASSIGNMENT_RULES:
      case DashboardView.SETTINGS_MANUAL_ASSIGNMENT:
      case DashboardView.SETTINGS_ADVISER_AVATAR:
      case DashboardView.SETTINGS_BILLING:
      case DashboardView.SETTINGS_BOTS:
      case DashboardView.SETTINGS_PROACTIVE_MESSAGES:
      case DashboardView.SETTINGS_TAGGING:
      case DashboardView.SETTINGS_TAGS:
      case DashboardView.SETTINGS_TAG_GROUPS:
      case DashboardView.SETTINGS_WIDGET_CONFIG:
      case DashboardView.SETTINGS_MESSAGES:
      case DashboardView.SETTINGS_WORKING_HOURS:
      case DashboardView.SETTINGS_WEBHOOKS:
      case DashboardView.SETTINGS_REPORTS:
      case DashboardView.SETTINGS_ROLES:
        return <SettingsContainer view={props.view} />;
      case DashboardView.REPORTS_EFFICIENCY:
      case DashboardView.REPORTS_SERVICE_LEVEL_TAG_GROUPS:
      case DashboardView.REPORTS_SERVICE_LEVEL_TAGS:
      case DashboardView.REPORTS_BACK_LINE_TAGS:
      case DashboardView.REPORTS_ADVISER_GROUPS:
      case DashboardView.REPORTS_ADVISER_TAG_GROUPS:
      case DashboardView.REPORTS_OTHER:
        const scope = props.match.path.split("/").reverse()[1] as ReportsScope;
        return scope && <Reports view={props.view} scope={scope} />;
      default:
        assertUnreachable(props.view);
        return;
    }
  };

  const renderContent = () => {
    const view = renderView();

    if (view) {
      return <div className={styles.content}>{view}</div>;
    }

    return;
  };

  const render = () => {
    return (
      <>
        {uiStore.upgradeModal && <ModalContainer />}
        <ToastArea />
        <div className={styles.dashboard} id={"dashboard-container"}>
          <CalendarDragOverlay />
          <AgentDashboardHeader view={props.view} />
          <ToastBar />
          {renderContent()}
        </div>
      </>
    );
  };

  return render();
});

const AgentDashboardContainerWithoutWidget = withErrorBoundary(withRouter(AgentDashboard), () => (
  <div className={styles.dashboard}>
    <DefaultErrorFallback />
  </div>
));

const AgentDashboardContainer: React.SFC<Props> = (props) => (
  <>
    <WidgetWrapper />
    <AgentDashboardContainerWithoutWidget {...props} />
  </>
);

export { AgentDashboardContainer };
